import { UserProfilePicture } from "@common/components/ProfilePicture/component";
import Badge from "@common/controls/Badge/component";
import OrganizationalDiagram from "@common/controls/Diagram/Basicprimitives/Organizational/component";
import { Basicprimitives } from "@common/controls/Diagram/Basicprimitives/types";
import Hint from "@common/controls/Hint/component";
import Icon from "@common/controls/Icon/component";
import { ModalHeaderButton } from "@common/controls/Modal/Header/Buttons";
import Modal from "@common/controls/Modal/component";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { usePromise } from "@common/lib/hooks";
import Network from "@common/modules/network/types";
import { Common } from "@common/types";
import classNames from "classnames";
import React from "react";
import { type DiagramItemComponentProps, formatVolumeNumber, type GenealogyItem, mapItem } from "./lib";

import "./style.less";

type ContentProps = {
  detail: Network.GenealogyCycleAggregatesBase | null;
  item: GenealogyItem;
  hideSensitiveDataTooltip?: string;
};
const Content = (props: ContentProps) => {
  const i18n = useI18n();

  const isInactive = React.useMemo(() => !props.item.commissionPercentage, [props.item.commissionPercentage]);
  return (
    <div
      className="items"
      style={{
        ["--color" as any]: props.item.color,
        opacity: props.item.isDeleted ? 0.5 : 1
      }}
    >
      <div className="item">
        <span className="title">{i18n.email}:</span>
        <span className="value">
          {props.hideSensitiveDataTooltip && props.item.isSensitive ? (
            <Hint tooltip={props.hideSensitiveDataTooltip} hint={i18n.emailHidden} />
          ) : (
            props.item.email
          )}
        </span>
      </div>
      <div className="item">
        <span className="title">{i18n.phone}:</span>
        <span className="value">
          {props.hideSensitiveDataTooltip && props.item.isSensitive ? (
            <Hint tooltip={props.hideSensitiveDataTooltip} hint={i18n.phoneHidden} />
          ) : (
            props.item.telephone
          )}
        </span>
      </div>
      <div className="item">
        <span className="title">{i18n.pinRank}:</span>
        <span className="value" style={{ color: props.item.pinRankColor }}>
          <Icon icon={Common.IconType.Crown} /> {props.item.pinRankName}
        </span>
      </div>
      <div className="item">
        <span className="title">{i18n.payRank}:</span>
        <span className="value" style={{ color: props.item.rankColor }}>
          <Icon icon={Common.IconType.Crown} /> {props.item.rankName}
        </span>
      </div>
      <div className="item">
        <span className="title">{i18n.membershipPoints}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.membershipPoints)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.personalPoints}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.personalPoints)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.incentivePoints}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.incentivePoints)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.commissionValue}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.commissionValue)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.retailValue}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.retailCommissionValue)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.income}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.earnings)}
        </InternalBadge>
      </div>
      <div className="item">
        <span className="title">{i18n.leadershipIncome}:</span>
        <InternalBadge color={props.item.color} isInactive={isInactive}>
          {formatVolumeNumber(props.detail?.leadershipEarnings)}
        </InternalBadge>
      </div>
    </div>
  );
};

type GenealogyModalBaseProps = DiagramItemComponentProps<GenealogyItem> & {
  detail: Network.Api.GenealogyDetail | null;
  hideSensitiveDataTooltip?: string;
  isLoading: boolean;
};
const GenealogyModalBase = (props: GenealogyModalBaseProps) => {
  const i18n = useI18n();

  return (
    <Modal
      className="genealogy_item narrow"
      isVisible
      isCentered
      isLoading={props.isLoading}
      onClose={props.exit}
      width={450}
    >
      <Modal.Header>
        {({ onCloseClick }) => (
          <>
            <div className="modal-title single-line-ellipsis">
              <Avatar item={props.item} />

              <div className="info">
                <h4 className="name">
                  {props.item.firstName} {props.item.lastName}
                </h4>
                {!!props.detail &&
                  (props.hideSensitiveDataTooltip && props.item.isSensitive ? (
                    <Hint tooltip={props.hideSensitiveDataTooltip} hint={i18n.addressHidden} />
                  ) : (
                    <span className="address" title={props.detail.addressLine}>
                      {props.detail.addressLine}
                    </span>
                  ))}
              </div>
            </div>

            <ModalHeaderButton onClick={onCloseClick} className="close" icon={IconType.Cross} />
          </>
        )}
      </Modal.Header>
      <Modal.Body>
        <Content hideSensitiveDataTooltip={props.hideSensitiveDataTooltip} item={props.item} detail={props.detail} />
      </Modal.Body>
    </Modal>
  );
};
export { GenealogyModalBase };

type GenealogyModalProps = DiagramItemComponentProps<GenealogyItem> & {
  isHost?: boolean;
  hideSensitiveDataTooltip?: string;
  getDetail: (item: Network.Genealogy) => Promise<Network.Api.GenealogyDetail>;
};
export const GenealogyModal = (props: GenealogyModalProps) => {
  const [data, isLoadingData] = usePromise({
    fn: props.getDetail,
    args: [props.item],
    shouldLoad: true
  });

  return <GenealogyModalBase {...props} detail={data} isLoading={isLoadingData} />;
};

export const Avatar = (props: { item: GenealogyItem }) => (
  <div className="avatar_container">
    <UserProfilePicture
      email={null}
      firstName={null}
      lastName={null}
      profilePictureMediaSourceId={props.item.userProfilePictureMediaSourceId}
      profilePictureMediaSourceKey={props.item.userProfilePictureMediaSourceKey}
      size={36}
    />

    {!!props.item.instance && <div className="instance">{props.item.instance}</div>}
  </div>
);
export type GenealogyNodeProps = { data: GenealogyItem };
export const GenealogyNode = (props: GenealogyNodeProps) => (
  <div
    className={classNames("genealogy_item", {
      deleted: props.data.isDeleted,
      inactive: !props.data.commissionPercentage
    })}
    style={{ "--color": props.data.color, height: 90, width: 280 } as any}
  >
    <Avatar item={props.data} />

    <div className="info">
      <div className="name single-line-ellipsis" title={`${props.data.firstName} ${props.data.lastName}`}>
        {props.data.firstName} {props.data.lastName}
      </div>
      <InternalBadge
        color={props.data.color}
        isInactive={!props.data.commissionPercentage}
        title={props.data.statusName}
      >
        {props.data.statusName}
      </InternalBadge>
      <span className="rank">
        <Icon icon={Common.IconType.Crown} /> {props.data.pinRankName}
      </span>
    </div>
  </div>
);

export type DiagramConfig = Partial<Basicprimitives.OrgDiagramConfig<any>> & {
  totalRecords?: number;
};

export interface GenealogyDiagramProps {
  data: Network.Genealogy[];
  cycleEndDate: string;
  getDetail: (item: Network.Genealogy) => Promise<Network.Api.GenealogyDetail>;
}
const GenealogyDiagram = (props: GenealogyDiagramProps) => {
  const i18n = useI18n();

  const [item, setItem] = React.useState<GenealogyItem | null>(null);
  const onItemClick = React.useCallback((item: GenealogyItem) => setItem(item), [setItem]);

  const exit = React.useCallback(() => setItem(null), [setItem]);

  const config = React.useMemo(
    () => ({
      items: props.data.map(mapItem),
      arrowsDirection: 2,
      calloutCornerRadius: 0,
      hasButtons: 0,
      normalLevelShift: 60,
      calloutOffset: 0,
      dotLevelShift: 30,
      dotItemsInterval: 6,
      templates: [
        {
          onItemRender: ({ context }) => <GenealogyNode data={context} />,
          onCursorRender: () => null,
          onHighlightRender: () => null,
          cursorBorderWidth: 0,
          highlightBorderWidth: 0,
          minimizedItemSize: {
            height: 12,
            width: 12
          },
          highlightPadding: {
            bottom: 0,
            top: 0,
            right: 0,
            left: 0
          },
          cursorPadding: {
            bottom: 0,
            top: 0,
            right: 0,
            left: 0
          },
          itemSize: {
            height: 90,
            width: 280
          },
          name: "genealogy"
        }
      ],
      calloutPointerWidth: "20%",
      calloutPlacementOffset: 50,
      connectorType: 0,
      normalItemsInterval: 30,
      autoSizeMinimum: {
        height: 90,
        width: 280
      },
      linesColor: "var(--diagramLinesColor)",
      horizontalAlignment: 0,
      buttonsPanelSize: 0,
      calloutOpacity: 0.1,
      hasSelectorCheckbox: 2,
      onButtonsRender: ({ context }) => (
        <Icon className="settings" icon={IconType.Cog} onClick={(e) => (e.stopPropagation(), onItemClick(context))} />
      ),
      cursorItem: null
    }),
    [props.data, onItemClick]
  );

  return (
    <>
      <OrganizationalDiagram config={config} />

      {item && (
        <GenealogyModal
          hideSensitiveDataTooltip={i18n.infoAffiliateNetworkHiddenData}
          getDetail={props.getDetail}
          item={item}
          exit={exit}
          parameters={{ CycleEndDate: props.cycleEndDate }}
          isHost
        />
      )}
    </>
  );
};

type InternalBadgeProps = {
  color: string;
  children: any;
  isInactive?: boolean;
  title?: string;
};
const InternalBadge = (props: InternalBadgeProps) => (
  <Badge
    color={props.isInactive ? SystemColor.Gray : SystemColor.White}
    style={props.isInactive ? undefined : ({ "--white": props.color } as any)}
    title={props.title}
  >
    {props.children}
  </Badge>
);

export default GenealogyDiagram;
